import * as React from 'react';

import type {
  CatalogProfileDetailDto,
  SeniorityLevelDto,
} from '@/api/catalog-client';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// =============================================================================
// profile-list.tsx — ZONE CENTRALE : liste des profils du référentiel (DBU §3.4 l.2714).
// =============================================================================
// DBU §3.4 l.2714 : chaque item affiche le CODE JeKo, le LIBELLÉ, la FAMILLE et des BADGES pour les
// niveaux d'expertise disponibles (Débutant / Maîtrisant / Expert / Référent = availableLevels).
// Consultation seule : cliquer un item OUVRE sa fiche latérale (état d'URL `profil`), aucune action
// d'écriture / export (DBU §3.4 l.2730/2736/2738).
//
// WCAG 2.1 AA : chaque profil est un <button> natif (navigation clavier), `aria-current` porte la
// sélection, chaque badge de niveau porte un LIBELLÉ TEXTE (jamais une couleur seule ; ST-NFR-03) ;
// l'état VIDE est annoncé via role="status".

/** Variant de badge par niveau (renfort visuel ; le LIBELLÉ texte reste le porteur d'info). */
const LEVEL_BADGE_VARIANT: Record<SeniorityLevelDto, BadgeProps['variant']> = {
  Débutant: 'outline',
  Maîtrisant: 'secondary',
  Expert: 'default',
  Référent: 'accent',
};

/** Rend les badges des niveaux disponibles d'un profil (texte porteur, couleur = renfort). */
function LevelBadges({
  levels,
}: {
  readonly levels: readonly string[];
}): React.JSX.Element {
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Niveaux d'expertise disponibles">
      {levels.map((level) => (
        <li key={level}>
          <Badge variant={LEVEL_BADGE_VARIANT[level as SeniorityLevelDto] ?? 'outline'}>
            {level}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

export interface ProfileListProps {
  readonly profiles: readonly CatalogProfileDetailDto[];
  /** Code du profil actuellement affiché dans la fiche (`undefined` = aucun). */
  readonly selectedProfileCode: string | undefined;
  /** Ouvre la fiche d'un profil (état d'URL `profil`). */
  readonly onSelectProfile: (profileCode: string) => void;
}

export function ProfileList({
  profiles,
  selectedProfileCode,
  onSelectProfile,
}: ProfileListProps): React.JSX.Element {
  if (profiles.length === 0) {
    return (
      <p
        role="status"
        className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground"
      >
        Aucun profil ne correspond à votre recherche. Ajustez ou réinitialisez les filtres.
      </p>
    );
  }

  return (
    <ul
      aria-label="Profils du référentiel"
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {profiles.map((profile) => {
        const selected = profile.profileCode === selectedProfileCode;
        return (
          <li key={profile.profileCode}>
            <button
              type="button"
              onClick={() => onSelectProfile(profile.profileCode)}
              aria-current={selected ? 'true' : undefined}
              className={cn(
                'flex w-full flex-col gap-2 rounded-lg border p-4 text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                selected
                  ? 'border-primary bg-accent/50'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50',
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-muted-foreground">
                  {profile.profileCode}
                </span>
                <span className="text-xs text-muted-foreground">{profile.famille}</span>
              </div>
              <span className="font-medium text-foreground">{profile.libelle}</span>
              <span className="text-xs text-muted-foreground">
                {profile.famille} · {profile.sousFamille}
              </span>
              <LevelBadges levels={profile.availableLevels} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
